import {
  type CSSProperties,
  type Key,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
  type RefObject,
  createContext,
  useContext,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { useNavigate } from 'react-router'
import { RawButton } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import { Icon } from '@/components/Icon'
import {
  AutoSkeleton,
  IsSkeletonContext,
  Skeleton,
  useSkeletonListContext,
} from '@/components/Skeleton'
import { dragFromPress, dragWidth } from '@/utils/drag-util'
import { CellText } from './CellText'
import styles from './DataTable.module.css'

export interface DataTableColumn<T> {
  id: string
  header: string
  width: number
  alignRight?: boolean
  /**
   * Current sort, or `null` when sortable but unsorted. Leave `undefined`
   * for columns that can't be sorted.
   */
  sortDirection?: 'asc' | 'desc' | null
  onSort?: () => void
  /**
   * Wraps the cell in the row's link, making the row reachable by keyboard.
   * Set it on the column that identifies the row.
   */
  linked?: boolean
  /**
   * Keeps the column in place when reordering. Other columns can't be dropped
   * before it.
   */
  locked?: boolean
  render: (row: T) => ReactNode
  /**
   * Stands in for the cell in a {@link DataTableRowSkeleton}. Defaults to a
   * line of text.
   */
  skeleton?: ReactNode
}

export interface DataTableSelection<T> {
  all: boolean | 'indeterminate'
  onToggleAll: () => void
  isSelected: (row: T) => boolean
  onToggle: (row: T, checked: boolean) => void
  isPending?: boolean
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: readonly T[]
  rowKey: (row: T) => Key
  /**
   * Where clicking a row goes. Rows without one aren't clickable. While
   * selecting, clicking a row toggles it instead.
   */
  rowHref?: (row: T) => string | undefined
  selection?: DataTableSelection<T>
  /**
   * Fixed trailing column, such as a row menu.
   */
  rowActions?: (row: T) => ReactNode
  onResize: (columnId: string, width: number) => void
  onResizeEnd: () => void
  /**
   * Called when a header is dragged to another position, with the column's
   * new index. Headers can only be dragged when it's set.
   */
  onReorder?: (columnId: string, toIndex: number) => void
  /**
   * Shown after the rows, inside the table so that
   * {@link DataTableRowSkeleton}s line up with its columns.
   */
  footer?: ReactNode
  /**
   * Renders the column headers as skeletons, while the first rows load.
   */
  isSkeleton?: boolean
  rowTestId?: (row: T) => string
}

interface ColumnDrag {
  header: string
  width: number
  /**
   * Where the lifted header, the dragged column and the drop slot are,
   * relative to the table. The header row is sticky, so the lift follows its
   * top.
   */
  liftTop: number
  liftLeft: number
  shadeLeft: number
  slotLeft: number
}

interface DataTableLayout {
  columns: { id: string; alignRight?: boolean; skeleton?: ReactNode }[]
  isSelectable: boolean
  hasRowActions: boolean
}

const DataTableLayoutContext = createContext<DataTableLayout | null>(null)

/**
 * Grid-based data table with resizable columns, for long lists such as jobs
 * and users.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowHref,
  selection,
  rowActions,
  onResize,
  onResizeEnd,
  onReorder,
  footer,
  isSkeleton,
  rowTestId,
}: DataTableProps<T>) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<ColumnDrag | null>(null)

  // The last column absorbs leftover width so the table always fills the card.
  const template = [
    ...(selection ? ['40px'] : []),
    ...columns.map((c, i) =>
      i === columns.length - 1 ? `minmax(${c.width}px, 1fr)` : `${c.width}px`,
    ),
    ...(rowActions ? ['48px'] : []),
  ].join(' ')

  return (
    <div
      ref={rootRef}
      className={styles.root}
      style={{ '--data-table-cols': template } as CSSProperties}
      role="table"
    >
      <IsSkeletonContext value={isSkeleton}>
        <div className={cx(styles.row, styles.headerRow)} role="row">
          {selection && (
            <div className={styles.cell} role="columnheader">
              <Checkbox
                onChange={selection.onToggleAll}
                checked={selection.all}
                isPending={selection.isPending}
              />
            </div>
          )}
          {columns.map((column, index) => (
            <HeaderCell
              key={column.id}
              column={column}
              onResize={onResize}
              onResizeEnd={onResizeEnd}
              onDragStart={
                onReorder && !column.locked && !isSkeleton
                  ? (e) =>
                      dragColumn(e, rootRef, columns, index, setDrag, onReorder)
                  : undefined
              }
            />
          ))}
          {rowActions && <div className={styles.cell} role="columnheader" />}
        </div>
      </IsSkeletonContext>
      {drag && (
        <>
          <div
            className={styles.shade}
            style={{ left: drag.shadeLeft, width: drag.width }}
          />
          <div className={styles.dropLine} style={{ left: drag.slotLeft }} />
          <div
            className={styles.lift}
            style={{
              top: drag.liftTop,
              left: drag.liftLeft,
              width: drag.width,
            }}
            aria-hidden
          >
            <Icon name="drag" size={12} className={styles.liftGrip} />
            <span className={styles.liftLabel}>{drag.header}</span>
          </div>
        </>
      )}
      {rows.map((row) => (
        <Row
          key={rowKey(row)}
          row={row}
          columns={columns}
          href={rowHref?.(row)}
          selection={selection}
          rowActions={rowActions}
          testId={rowTestId?.(row)}
        />
      ))}
      <DataTableLayoutContext
        value={{
          columns,
          isSelectable: !!selection,
          hasRowActions: !!rowActions,
        }}
      >
        {footer}
      </DataTableLayoutContext>
    </div>
  )
}

/**
 * A placeholder row for a row still loading, laid out like the
 * {@link DataTable} it's in the footer of. Render it inside a
 * `SkeletonList` or `SkeletonListInfiniteScrollTrigger`, which vary it per
 * row and connect the trigger's ref.
 */
export function DataTableRowSkeleton() {
  const layout = useContext(DataTableLayoutContext)
  const { ref } = useSkeletonListContext()
  if (!layout) {
    throw new Error('Missing data table layout context')
  }

  return (
    <div
      ref={ref as Ref<HTMLDivElement> | undefined}
      className={styles.row}
      aria-hidden
    >
      {layout.isSelectable && <div className={styles.cell} />}
      {layout.columns.map((column) => (
        <div
          key={column.id}
          className={cx(
            styles.cell,
            styles.bodyCell,
            column.alignRight && styles.alignRight,
          )}
        >
          {column.skeleton ?? <CellTextSkeleton />}
        </div>
      ))}
      {layout.hasRowActions && <div className={styles.cell} />}
    </div>
  )
}

function CellTextSkeleton() {
  const { varyPercent } = useSkeletonListContext()
  return <Skeleton width={varyPercent(50)} />
}

/**
 * Drags the header at `from` along the header row from its `pointerdown`,
 * showing the lift, the column and the drop slot in `setDrag`, and reorders on
 * release.
 */
function dragColumn<T>(
  e: PointerEvent<HTMLElement>,
  rootRef: RefObject<HTMLElement | null>,
  columns: DataTableColumn<T>[],
  from: number,
  setDrag: (drag: ColumnDrag | null) => void,
  onReorder: (columnId: string, toIndex: number) => void,
) {
  const root = rootRef.current
  if (!root) {
    return
  }
  const headerCells = () =>
    Array.from(
      root.querySelectorAll<HTMLElement>('[role="columnheader"][data-column]'),
      (cell) => cell.getBoundingClientRect(),
    )
  const firstSlot = columns.reduce((n, c, i) => (c.locked ? i + 1 : n), 0)
  const start = headerCells()[from]
  const grabOffset = e.clientX - start.left
  let slot = from

  const show = (pointerX: number) => {
    const rootBox = root.getBoundingClientRect()
    const rootLeft = rootBox.left
    const cells = headerCells()
    slot = firstSlot
    cells.forEach((cell, i) => {
      if (i >= firstSlot && pointerX > cell.left + cell.width / 2) {
        slot = i + 1
      }
    })
    const edge = slot === 0 ? cells[0].left : cells[slot - 1].right
    setDrag({
      header: columns[from].header,
      width: cells[from].width,
      liftTop: cells[from].top - rootBox.top,
      liftLeft: pointerX - grabOffset - rootLeft,
      shadeLeft: cells[from].left - rootLeft,
      slotLeft: edge - rootLeft - 1,
    })
  }

  dragFromPress(e, {
    onMove: (ev) => show(ev.clientX),
    onEnd: () => {
      setDrag(null)
      // The slot counts the dragged column itself, which leaves its place.
      const to = slot > from ? slot - 1 : slot
      if (to !== from) {
        onReorder(columns[from].id, to)
      }
    },
    onCancel: () => setDrag(null),
  })
}

interface HeaderCellProps<T> {
  column: DataTableColumn<T>
  onResize: (columnId: string, width: number) => void
  onResizeEnd: () => void
  /**
   * Set when the column can be dragged to reorder it.
   */
  onDragStart?: (e: PointerEvent<HTMLElement>) => void
}

function HeaderCell<T>({
  column,
  onResize,
  onResizeEnd,
  onDragStart,
}: HeaderCellProps<T>) {
  const direction = column.sortDirection
  const sortable = direction !== undefined

  return (
    <div
      className={cx(
        styles.cell,
        styles.headerCell,
        column.alignRight && styles.alignRight,
        onDragStart && styles.draggable,
      )}
      role="columnheader"
      aria-sort={
        direction === 'asc'
          ? 'ascending'
          : direction === 'desc'
            ? 'descending'
            : undefined
      }
      data-column={column.id}
      onPointerDown={onDragStart}
    >
      {onDragStart && (
        <span className={styles.grip}>
          <Icon name="drag" size={12} />
        </span>
      )}
      {sortable ? (
        <RawButton
          className={cx(styles.sortButton, direction && styles.sorted)}
          onPress={column.onSort}
        >
          <AutoSkeleton>{column.header}</AutoSkeleton>
          <span className={styles.sortIcon}>
            <Icon
              size={12}
              name="sorting"
              rotate={direction === 'asc' ? -180 : 0}
            />
          </span>
        </RawButton>
      ) : (
        <CellText variant="truncate">
          <AutoSkeleton>{column.header}</AutoSkeleton>
        </CellText>
      )}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- the click handler only keeps resize clicks from reaching the sort button */}
      <span
        className={styles.resizeHandle}
        onPointerDown={(e) => {
          e.stopPropagation()
          dragWidth(
            e,
            column.width,
            (width) => onResize(column.id, width),
            onResizeEnd,
          )
        }}
        onClick={(e) => e.stopPropagation()}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${column.header} column`}
      />
    </div>
  )
}

interface RowProps<T> {
  row: T
  columns: DataTableColumn<T>[]
  href?: string
  selection?: DataTableSelection<T>
  rowActions?: (row: T) => ReactNode
  testId?: string
}

function Row<T>({
  row,
  columns,
  href,
  selection,
  rowActions,
  testId,
}: RowProps<T>) {
  const navigate = useNavigate()

  const open = (e: MouseEvent) => {
    if (href && (e.metaKey || e.ctrlKey)) {
      window.open(href, '_blank')
      return
    }
    if (selection) {
      selection.onToggle(row, !selection.isSelected(row))
      return
    }
    if (href) {
      void navigate(href)
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- keyboard users open the row through its focusable linked cell, whose click bubbles here
    <div
      className={cx(styles.row, (href || selection) && styles.clickable)}
      role="row"
      onClick={open}
      data-test={testId}
    >
      {selection && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- only stops clicks on the checkbox from opening the row
        <div
          className={styles.cell}
          role="cell"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selection.isSelected(row)}
            onChange={(checked) => selection.onToggle(row, checked)}
          />
        </div>
      )}
      {columns.map((column) => (
        <div
          key={column.id}
          role="cell"
          className={cx(
            styles.cell,
            styles.bodyCell,
            column.alignRight && styles.alignRight,
          )}
        >
          {href && column.linked ? (
            <a
              href={href}
              className={styles.rowLink}
              onClick={(e) => e.preventDefault()}
            >
              {column.render(row)}
            </a>
          ) : (
            column.render(row)
          )}
        </div>
      ))}
      {rowActions && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- only stops clicks on row actions from opening the row
        <div
          className={cx(styles.cell, styles.alignRight)}
          role="cell"
          onClick={(e) => e.stopPropagation()}
        >
          {rowActions(row)}
        </div>
      )}
    </div>
  )
}
